// Port of the composer's attachment rules on the Apple app:
//   - staging — ios/Sources/ViewModels/ChannelViewModel.swift `stageAttachment` (count first, then
//     readable, then size; every refusal SPOKEN, never a silent drop);
//   - a pasted picture's name — ios/SourcesMac/MacComposerPaste.swift `attachmentURLs`
//     ("imagen-<8 of a UUID>.png");
//   - the file card's badge — ios/Sources/Views/AttachmentViews.swift `FileCardView.badge`;
//   - name sanitising — ios/Sources/Sync/AttachmentOutbox.swift `safe(_:)`;
//   - "a bare attachment is a complete message" — ChannelViewModel.sendMessage's guard.
//
// Web differences, deliberate: the LIMITS are the server's (FileSettings.MaxFileSize from the client
// config; Mattermost's 10 files per post) instead of the apps' hard-coded 5 files / 50 MB — the
// browser replaces the webapp, whose users already live with the server's limits. 50 MB stays as
// the fallback while the config has not been read. And the web uploads while the file sits in the
// tray (the apps upload inside the send), so the send gate knows two more states: still uploading
// (the send is QUEUED behind the uploads, as the apps' send waits for its own uploads) and an upload
// that failed (blocks the send until it is retried or removed — a message that silently lost a file
// would be worse than one that waits).

/** The apps' rule (Mac ChannelViewModel.stageAttachment, Android, Windows): five files per message.
 *  Mattermost itself allows 10; every Satori client stops at 5 (user decision 2026-10-12). */
export const MAX_FILES_PER_POST = 5;
/** The apps' 50 MB ceiling — and the cap even when the server allows more. */
export const FALLBACK_MAX_FILE_SIZE = 50 * 1024 * 1024;

export interface Limits {
  maxFiles: number;
  maxFileSize: number;
}

/** The apps' limits, never above the server's MaxFileSize (absent or garbage → 50 MB). */
export function limitsFromConfig(maxFileSize: string | null | undefined): Limits {
  const n = Number(maxFileSize);
  return { maxFiles: MAX_FILES_PER_POST, maxFileSize: Number.isFinite(n) && n > 0 ? Math.min(Math.floor(n), FALLBACK_MAX_FILE_SIZE) : FALLBACK_MAX_FILE_SIZE };
}

export type Refusal = 'tooMany' | 'tooLarge' | 'unreadable';

export interface Candidate {
  name: string;
  size: number;
  /** A dropped folder (DataTransferItem.webkitGetAsEntry().isDirectory) — never uploadable. */
  isDirectory?: boolean;
}

export interface Admission {
  /** Indexes into the candidates, in their order. */
  accepted: number[];
  refused: Array<{ index: number; reason: Refusal }>;
}

/**
 * Which of `candidates` may join a tray that already holds `stagedCount` files. Same order of
 * checks as `stageAttachment`: the count first (the eleventh file is "too many" whatever its size),
 * then readability, then size.
 */
export function admit(stagedCount: number, candidates: Candidate[], limits: Limits): Admission {
  const out: Admission = { accepted: [], refused: [] };
  let count = stagedCount;
  candidates.forEach((c, index) => {
    if (count >= limits.maxFiles) out.refused.push({ index, reason: 'tooMany' });
    else if (c.isDirectory) out.refused.push({ index, reason: 'unreadable' });
    else if (c.size > limits.maxFileSize) out.refused.push({ index, reason: 'tooLarge' });
    else {
      out.accepted.push(index);
      count += 1;
    }
  });
  return out;
}

/**
 * ONE sentence for a batch with refusals (the Mac shows one staging error line, the last one): the
 * first refusal names the file, `others` counts the rest so nothing is hidden.
 */
export function refusalNotice(refused: Array<{ name: string; reason: Refusal }>): { reason: Refusal; name: string; others: number } | null {
  if (!refused.length) return null;
  // "Too many" is about the batch, not a file: it wins, because removing files is the fix.
  const lead = refused.find((r) => r.reason === 'tooMany') ?? refused[0];
  return { reason: lead.reason, name: lead.name, others: refused.length - 1 };
}

const IMAGE_EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/tiff': 'tiff',
  'image/heic': 'heic',
  'image/avif': 'avif',
};

/**
 * The name a browser gives clipboard image DATA ("image.png" in Chrome/Edge, "image.png"/"blob" in
 * Firefox, "Pasted image.png" elsewhere) — a picture with no real name of its own.
 */
export function isGenericPastedName(name: string): boolean {
  const n = name.trim();
  if (!n) return true;
  return /^(image|imagen|blob|clipboard|pasted image|screenshot)(\s*\(?\d+\)?)?(\.[a-z0-9]{2,5})?$/i.test(n);
}

/** MacComposerPaste: "imagen-" + the first 8 characters of a UUID, with the picture's own type. */
export function pastedImageName(mimeType: string, uuid: string): string {
  const ext = IMAGE_EXTENSIONS[mimeType.toLowerCase()] ?? 'png';
  return `imagen-${uuid.replace(/-/g, '').slice(0, 8).toUpperCase()}.${ext}`;
}

/**
 * The name a pasted file is staged under: a real file (copied in the file manager) keeps its name;
 * picture data gets the Mac's "imagen-XXXXXXXX.png".
 */
export function nameForPaste(name: string, mimeType: string, uuid: string): string {
  if (mimeType.toLowerCase().startsWith('image/') && isGenericPastedName(name)) return pastedImageName(mimeType, uuid);
  return safeName(name);
}

/**
 * Whether a paste is ATTACHMENTS or text. MacComposerPaste takes files first — but a browser's
 * clipboard also carries a rendered PICTURE of copied text (Excel, Word, Numbers put one beside the
 * table), and attaching that would eat the table PastedTable would have kept. So: real files (a
 * name of their own, or not a picture) always attach; picture data attaches only when the
 * clipboard holds no plain text to paste instead.
 */
export function pasteWantsFiles(plainText: string, files: Array<{ name: string; mimeType: string }>): boolean {
  if (!files.length) return false;
  const realFile = files.some((f) => !f.mimeType.toLowerCase().startsWith('image/') || !isGenericPastedName(f.name));
  return realFile || plainText.trim() === '';
}

/** AttachmentOutbox.safe: no path separators, no NULs, never empty. */
export function safeName(name: string): string {
  const cleaned = name.replace(/\//g, '_').replace(/\0/g, '');
  return cleaned === '' ? 'file' : cleaned;
}

/** FileCardView.badge: the extension, upper-cased, at most 4 letters; "FILE" when there is none. */
export function fileBadge(name: string): string {
  const component = name.slice(name.lastIndexOf('/') + 1);
  const dot = component.lastIndexOf('.');
  const ext = dot <= 0 ? '' : component.slice(dot + 1);
  return ext === '' ? 'FILE' : ext.toUpperCase().slice(0, 4);
}

export type UploadState = 'uploading' | 'done' | 'failed';

/** 0…1 for the tray's bar; an unknown total reads as "just started", never NaN. */
export function progressFraction(loaded: number, total: number): number {
  if (!(total > 0)) return 0;
  return Math.max(0, Math.min(1, loaded / total));
}

/**
 * What the send button may do. "A bare attachment with no text is a perfectly normal message;
 * nothing at all is not" (ChannelViewModel.sendMessage).
 *  - empty: nothing to send;
 *  - ready: send now;
 *  - queued: send now — the message appears at once and posts when its uploads land;
 *  - blocked: an upload failed; retry or remove it first.
 */
export type SendGate = 'empty' | 'ready' | 'queued' | 'blocked';

export function sendGate(text: string, uploads: UploadState[]): SendGate {
  if (uploads.includes('failed')) return 'blocked';
  if (text.trim() === '' && uploads.length === 0) return 'empty';
  if (uploads.includes('uploading')) return 'queued';
  return 'ready';
}

/** PDFs open in the browser's own viewer; every other non-image file downloads. */
export function opensInBrowser(mimeType: string, name: string): boolean {
  return mimeType.toLowerCase() === 'application/pdf' || /\.pdf$/i.test(name);
}

export const AttachmentStaging = {
  MAX_FILES_PER_POST,
  FALLBACK_MAX_FILE_SIZE,
  limitsFromConfig,
  admit,
  refusalNotice,
  isGenericPastedName,
  pastedImageName,
  nameForPaste,
  pasteWantsFiles,
  safeName,
  fileBadge,
  progressFraction,
  sendGate,
  opensInBrowser,
} as const;
